import { useEffect, useState } from "react";
import { Mail, Send, Loader2, ShieldAlert } from "lucide-react";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const escapeHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const toBodyHtml = (text) =>
  String(text || "")
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br/>")}</p>`)
    .join("");

const ROLE_OPTIONS = [
  { value: "all", label: "All real accounts (GPs + clinicians)" },
  { value: "gp", label: "GPs only" },
  { value: "clinician", label: "Clinicians only" },
];

const CONFIRM_PHRASE = "SEND UPDATE";

export const AdminBulkEmail = () => {
  const [role, setRole] = useState("all");
  const [recipientCount, setRecipientCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingLive, setSendingLive] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingCount(true);
    authedFetch(`/admin/bulk-email/recipients?role=${role}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setRecipientCount(data.count);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoadingCount(false));
    return () => {
      cancelled = true;
    };
  }, [role]);

  const canSend = subject.trim() && message.trim();

  const sendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Enter an email address to send the test to.");
      return;
    }
    setSendingTest(true);
    try {
      const res = await authedFetch("/admin/bulk-email/send", {
        method: "POST",
        body: JSON.stringify({
          subject,
          body_html: toBodyHtml(message),
          role,
          test_email: testEmail.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Could not send the test email.");
        return;
      }
      toast.success(data.sent ? `Test email sent to ${testEmail.trim()}.` : "Test email could not be delivered.");
    } finally {
      setSendingTest(false);
    }
  };

  const sendLive = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error(`Type "${CONFIRM_PHRASE}" to confirm.`);
      return;
    }
    setSendingLive(true);
    try {
      const res = await authedFetch("/admin/bulk-email/send", {
        method: "POST",
        body: JSON.stringify({
          subject,
          body_html: toBodyHtml(message),
          role,
          confirm: confirmText,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Could not send the update.");
        return;
      }
      setLastResult(data);
      setConfirmText("");
      toast.success(`Sent to ${data.sent} of ${data.recipients} recipient(s).`);
    } finally {
      setSendingLive(false);
    }
  };

  return (
    <section className="space-y-5" data-testid="admin-bulk-email">
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900">Launch email</h2>
        <p className="mt-1 text-sm text-slate-500">
          Send a one-off update to real, signed-up GP and clinician accounts. Test accounts are never included.
        </p>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5 space-y-4">
        <div>
          <Label>Recipients</Label>
          <select
            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            {loadingCount ? "Counting real accounts…" : recipientCount === null ? "" : `${recipientCount} real account(s) will receive this.`}
          </p>
        </div>

        <div>
          <Label>Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="An update from SimplyRefer" className="mt-1" />
        </div>

        <div>
          <Label>Message</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={"Hi {name},\n\nWrite your update here. Leave a blank line between paragraphs."}
            className="mt-1 min-h-[160px]"
          />
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5 space-y-3">
        <h3 className="font-heading text-sm font-semibold text-slate-900">Send a test</h3>
        <p className="text-xs text-slate-500">Send yourself a preview before sending to real accounts.</p>
        <div className="flex flex-wrap gap-2">
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@simplyrefer.com.au" className="max-w-xs" />
          <Button type="button" variant="outline" disabled={!canSend || sendingTest} onClick={sendTest}>
            {sendingTest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            Send test
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50/60 p-5 space-y-3">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-amber-900">
          <ShieldAlert className="h-4 w-4" /> Send to real accounts
        </h3>
        <p className="text-xs text-amber-800">
          This emails every real (non-test) account matching your recipient selection above. This cannot be undone. Type{" "}
          <span className="font-mono font-semibold">{CONFIRM_PHRASE}</span> to confirm.
        </p>
        <div className="flex flex-wrap gap-2">
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={CONFIRM_PHRASE} className="max-w-xs" />
          <Button
            type="button"
            disabled={!canSend || confirmText !== CONFIRM_PHRASE || sendingLive}
            onClick={sendLive}
          >
            {sendingLive ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send to {recipientCount ?? "…"} account(s)
          </Button>
        </div>
        {lastResult && (
          <p className="text-xs text-amber-900">
            Last send: {lastResult.sent} of {lastResult.recipients} delivered{lastResult.failed?.length ? `, ${lastResult.failed.length} failed` : ""}.
          </p>
        )}
      </div>
    </section>
  );
};
